import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Eye, CheckCircle, AlertTriangle } from "lucide-react"
import type { JSONSchema } from "@/api/types/schema"
import type { MonitorAnalysis } from "@/types/bill"
import type { APISession } from "@/types/session"
import { formatSchemaForDisplay } from "@/utils/schema/schema-utils"
import { BillImpactSections } from "@/components/BillImpactSections"

interface SchemaPreviewProps {
  schema: JSONSchema | null
  validation: { valid: boolean; errors: string[] }
}

// Generate sample analysis data based on schema structure
function generateSampleAnalysis(schema: JSONSchema): MonitorAnalysis {
  const sampleAnalysis: MonitorAnalysis = {
    title: String(schema.properties.title?.const || 'Analysis Results')
  }

  // Sample score values and types for demonstration
  const sample_scores = [
    { score: 8, type: 'positive' as const },
    { score: 3, type: 'negative' as const },
    { score: 5, type: 'neutral' as const },
    { score: 7, type: 'positive' as const },
    { score: 2, type: 'negative' as const },
    { score: 6, type: 'neutral' as const }
  ]

  const sample_rationales = [
    "This bill would have a significant positive impact on the organization's operations by streamlining processes and reducing administrative burden.",
    "The proposed changes could create compliance challenges that may require additional resources and training for staff.",
    "While the impact is moderate, the bill addresses important regulatory gaps that could affect long-term sustainability.",
    "This legislation aligns well with current strategic priorities and could enhance operational efficiency.",
    "The financial implications of this bill may strain current budget allocations and require careful planning.",
    "This bill provides a balanced approach that addresses key concerns while maintaining operational flexibility."
  ]

  let score_index = 0
  let rationale_index = 0

  // Process each section defined in the schema
  for (const [section_id, section_schema] of Object.entries(schema.properties)) {
    if (section_id === 'title' || !section_schema.properties) continue

    const section_data: any = {
      title: String(section_schema.properties.title?.const || section_id)
    }

    // Add icon if specified
    if (section_schema.properties.icon?.const) {
      section_data.icon = section_schema.properties.icon.const
    }

    // Add order if specified
    if (section_schema.properties.order?.const) {
      section_data.order = section_schema.properties.order.const
    }

    // Add details_title if specified
    if (section_schema.properties.details_title?.const) {
      section_data.details_title = section_schema.properties.details_title.const
    }

    // Add score properties
    for (const [prop_key, prop_schema] of Object.entries(section_schema.properties)) {
      if (prop_key === 'title' || prop_key === 'icon' || prop_key === 'order' || 
          prop_key === 'details_title' || prop_key === 'rationale') continue

      if (prop_schema.type === 'object' && prop_schema.properties?.score?.type === 'integer') {
        const sample_score = sample_scores[score_index % sample_scores.length]
        section_data[prop_key] = {
          title: String(prop_schema.properties.title?.const || prop_key),
          score: sample_score.score,
          type: sample_score.type
        }
        score_index++
      }
    }

    // Add rationale if specified
    if (section_schema.properties.rationale && section_schema.required?.includes('rationale')) {
      section_data.rationale = sample_rationales[rationale_index % sample_rationales.length]
      rationale_index++
    }

    sampleAnalysis[section_id] = section_data
  }

  return sampleAnalysis
}

export function SchemaPreview({ schema, validation }: SchemaPreviewProps): JSX.Element {
  if (!schema) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Eye className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>No schema to preview</p>
        <p className="text-sm">Configure sections in the builder to see the generated schema</p>
      </div>
    )
  }

  const formatted_schema = formatSchemaForDisplay(schema)
  const sample_analysis = generateSampleAnalysis(schema)

  // Create a mock bill object for the preview
  const sample_bill = {
    id: 'sample-bill-id',
    bill_number: 'HB 123',
    title: 'Sample Bill for Schema Preview',
    synopsis: 'This is a sample bill used to demonstrate the analysis rendering',
    status: 'Active',
    chamber: 'House',
    crossfile_bill_number: null,
    session: '2024RS' as APISession,
    last_modified: new Date().toISOString(),
    pdf_url: 'https://example.com/sample-bill.pdf',
    full_text_available: true,
    impact_score: 7,
    monitor_analysis: sample_analysis
  }

  return (
    <div className="space-y-6">
      {/* Validation Status */}
      <Alert variant={validation.valid ? "default" : "destructive"}>
        {validation.valid ? (
          <CheckCircle className="h-4 w-4" />
        ) : (
          <AlertTriangle className="h-4 w-4" />
        )}
        <AlertDescription>
          {validation.valid ? (
            <div className="flex items-center gap-2">
              <span className="font-medium">Schema is valid</span>
              <Badge variant="outline" className="text-xs">
                Ready to use
              </Badge>
            </div>
          ) : (
            <div className="space-y-1">
              <div className="font-medium">Schema validation failed:</div>
              <ul className="list-disc list-inside text-sm space-y-1">
                {validation.errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}
        </AlertDescription>
      </Alert>

      {/* Analysis Rendering Preview */}
      {validation.valid && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Analysis Preview
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              This is how the analysis will appear when rendered with your schema configuration
            </p>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg p-4 bg-gray-50">
              <BillImpactSections bill={sample_bill} />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Raw Schema */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Raw JSON Schema</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <pre className="text-xs font-mono bg-gray-50 p-4 rounded-lg overflow-x-auto max-h-96">
              {formatted_schema}
            </pre>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}